import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MdApps,
  MdAutorenew,
  MdOutlineQueue,
  MdCheckCircleOutline,
  MdErrorOutline,
} from "react-icons/md";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { moveQueueStatus, resetQueueStats, saveQueueStats } from "../../features/Queue/QueueSlice";
import { saveHeaderTitle } from "../../features/HomePage/HomePageSlice";
import { triggerGenerationCountRefresh } from "../Generation/useGenerationCount";

export const getFilterIcon = (key) => {
  switch (key) {
    case "processing":
      return MdAutorenew;
    case "queued":
      return MdOutlineQueue;
    case "completed":
      return MdCheckCircleOutline;
    case "failed":
      return MdErrorOutline;
    default:
      return MdApps;
  }
};

export const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "processing", label: "Processing" },
  { key: "queued", label: "Queued" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

const ACTIVE_STATUSES = new Set(["processing", "queued"]);

const getValue = (item, selector) => {
  try {
    return selector(item);
  } catch {
    return undefined;
  }
};

const firstDefined = (item, selectors) => {
  for (const selector of selectors) {
    const value = getValue(item, selector);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const asString = (value, fallback = "") => {
  if (typeof value === "string" && value.trim()) return value;
  if (value === 0) return "0";
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return asString(value);
  }
  return date.toLocaleString();
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getStatus = (item) =>
  asString(
    firstDefined(item, [
      (entry) => entry?.sStatus,
      (entry) => entry?.status,
      (entry) => entry?.queueStatus,
    ]),
    "queued"
  ).toLowerCase();

const getImage = (item, selectors, fallback) => {
  const value = firstDefined(item, selectors);
  if (typeof value === "string" && value.trim()) return value;
  if (value?.front) return value.front;
  if (value?.image) return value.image;
  return fallback;
};

const getQueueItemId = (item) =>
  firstDefined(item, [
    (entry) => entry?.id,
    (entry) => entry?.iQueueId,
    (entry) => entry?.iQueueTaskId,
    (entry) => entry?.queue_task_id,
    (entry) => entry?.queueTaskId,
  ]);

const parseMaybeJson = (value) => {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getGenerationParameters = (item) => {
  const rawValue = firstDefined(item, [
    (entry) => entry?.generation_params,
    (entry) => entry?.generationParams,
    (entry) => entry?.aGenerationParams,
    (entry) => entry?.image_data,
    (entry) => entry?.imageData,
    (entry) => entry?.sImageData,
    (entry) => entry?.sGenerationConfig,
  ]);

  const parsed = parseMaybeJson(rawValue);
  const nestedGenerationParams = parseMaybeJson(
    parsed?.generation_params ?? parsed?.generationParams ?? parsed?.aGenerationParams
  );

  if (nestedGenerationParams && typeof nestedGenerationParams === "object") {
    return nestedGenerationParams;
  }

  return parsed && typeof parsed === "object" ? parsed : {};
};

const normalizeGenerationValue = (value) => {
  if (value === "" || value === null || value === undefined) return "Default";
  if (typeof value === "string" && !value.trim()) return "Default";
  return value;
};

const normalizeGenerationParameters = (parameters = {}) => {
  if (!parameters || typeof parameters !== "object") return {};

  return Object.entries(parameters).reduce((accumulator, [key, value]) => {
    accumulator[key] = normalizeGenerationValue(value);
    return accumulator;
  }, {});
};

const normalizeQueueItem = (item) => {
  const status = getStatus(item);
  const previewImageValue = firstDefined(item, [(entry) => entry?.preview_image_url]);
  const generatedImageValue = firstDefined(item, [(entry) => entry?.generated_image_url]);
  const displayImageValue = previewImageValue || generatedImageValue;

  const productImage = getImage(item, [(entry) => entry?.product_front_image], "");
  const productBackImage = getImage(item, [(entry) => entry?.product_back_image], "");
  const modelImage = getImage(item, [(entry) => entry?.model_front_image], "");
  const modelBackImage = getImage(item, [(entry) => entry?.model_back_image], "");

  const generationParameters = normalizeGenerationParameters(getGenerationParameters(item));
  const pose = String(
    firstDefined(item, [
      (entry) => entry?.pose,
      (entry) => entry?.sPose,
      (entry) => entry?.generation_pose,
      (entry) => entry?.image_data?.pose,
      (entry) => entry?.imageData?.pose,
    ]) || ""
  ).toLowerCase();

  const isBackPose = pose === "back";
  const displayModelImage = isBackPose ? modelBackImage || modelImage : modelImage;
  const backgroundImage = getImage(item, [(entry) => entry?.background_image], "");

  const progress = toNumber(
    firstDefined(item, [(entry) => entry?.progress]),
    status === "completed" ? 100 : 0
  );

  const rawErrorMessage = asString(
    firstDefined(item, [(entry) => entry?.error_message, (entry) => entry?.errorMessage]),
    ""
  );

  const generationStatus = asString(
    firstDefined(item, [
      (entry) => entry?.generation_status,
      (entry) => entry?.generationStatus,
    ]),
    ""
  ).toLowerCase();

  const displayErrorMessage =
    status === "failed" || generationStatus === "failed"
      ? "We couldn't generate the image right now. Please try again after some time."
      : rawErrorMessage;

  return {
    id: getQueueItemId(item),
    status,
    progress,
    product: {
      image: productImage,
      back: productBackImage,
      name: asString(firstDefined(item, [(entry) => entry?.product_title]), "Product"),
    },
    model: modelImage
      ? {
          image: displayModelImage,
          back: modelBackImage,
          name: asString(firstDefined(item, [(entry) => entry?.model_name]), "Model"),
        }
      : null,
    background: backgroundImage
      ? {
          image: backgroundImage,
          name: asString(firstDefined(item, [(entry) => entry?.background_name]), "Background"),
        }
      : null,
    result: {
      image: displayImageValue,
      generatedImage: generatedImageValue,
    },
    showLoader: status === "processing",
    showProgress: status === "processing" && progress > 0 && progress < 100,
    errorMessage: displayErrorMessage,
    displayErrorMessage,
    error_message: rawErrorMessage,
    generation_status: generationStatus,
    queueTaskId: getQueueItemId(item),
    generationParameters,
    executionTime:
      status === "completed"
        ? firstDefined(item, [
            (entry) => entry?.execution_time,
            (entry) => entry?.executionTime,
          ])
        : null,
    startTime: firstDefined(item, [
      (entry) => entry?.createdAt,
      (entry) => entry?.created_at,
      (entry) => entry?.sCreatedAt,
      (entry) => entry?.updatedAt,
    ]),
    date: formatDate(
      firstDefined(item, [
        (entry) => entry?.createdAt,
        (entry) => entry?.created_at,
        (entry) => entry?.sCreatedAt,
        (entry) => entry?.updatedAt,
      ])
    ),
    buttons: [],
    raw: item,
  };
};

const extractQueueItems = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  return [];
};

const extractQueuePaginationMeta = (response, fallbackLimit) => {
  const payload = response?.data?.data ?? response?.data ?? response ?? {};
  const limit = Number(payload?.limit) || fallbackLimit;
  const total = Number(payload?.total) || 0;
  const page = Number(payload?.page) || 1;
  const totalPages = Math.max(1, Number(payload?.totalPages) || Math.ceil(total / Math.max(1, limit)) || 1);

  return { page, limit, total, totalPages };
};

const buildQueueListUrl = ({ page, limit, search, status }) => {
  const query = new URLSearchParams();
  query.set("page", page);
  query.set("limit", limit);

  if (search) query.set("search", search);
  if (status) {
    if (Array.isArray(status)) {
      const valid = status.filter((s) => s && String(s).toLowerCase() !== "all");
      if (valid.length > 0) query.set("status", valid.join(","));
    } else if (status !== "all") {
      query.set("status", status);
    }
  }

  const queryString = query.toString().replace(/%2C/gi, ",");
  return `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}?${queryString}`;
};

const normalizeQueueStateItem = (item) => {
  const normalizedStatus = getStatus(item);
  const normalizedId = getQueueItemId(item);

  return {
    ...item,
    id: normalizedId,
    status: normalizedStatus,
  };
};

const mergeQueueTerminalStatus = (item, queueId, statusResult) => {
  const payload = statusResult?.payload || {};
  const terminalStatus = asString(statusResult?.status, getStatus(payload) || getStatus(item)).toLowerCase();
  const terminalImage = extractGeneratedImage(payload);
  const normalizedQueueId = String(queueId);

  return {
    ...item,
    status: terminalStatus || item.status,
    sStatus: terminalStatus || item.sStatus,
    progress: terminalStatus === "completed" ? 100 : 0,
    error_message:
      payload?.error_message ??
      payload?.errorMessage ??
      (terminalStatus === "failed" || terminalStatus === "error" ? item.errorMessage || "Generation failed" : item.error_message),
    generated_image_url: terminalImage || null,
    preview_image_url: terminalStatus === "completed" ? terminalImage || null : null,
    execution_time: payload?.execution_time ?? payload?.executionTime ?? item.execution_time,
    result: {
      ...(item.result || {}),
      image: terminalStatus === "completed" ? terminalImage || "" : "",
      generatedImage: terminalStatus === "completed" ? terminalImage || "" : "",
    },
    id: item.id ?? normalizedQueueId,
    queueTaskId: item.queueTaskId ?? normalizedQueueId,
  };
};

const extractGeneratedImage = (payload) => {
  const candidate =
    payload?.sGeneratedImageUrl ||
    payload?.generatedImageUrl ||
    payload?.generated_image_url ||
    payload?.result?.image ||
    payload?.data?.sGeneratedImageUrl ||
    payload?.data?.generatedImageUrl;

  return asString(candidate, "");
};

const buildQueuePayloadFromItem = (item) => {
  const raw = item?.raw || item || {};

  const resolvedGenderId = firstDefined(raw, [(entry) => entry?.iGenderId]);
  const resolvedModeId = firstDefined(raw, [(entry) => entry?.iModeId]);
  const resolvedModelId = firstDefined(raw, [(entry) => entry?.iModelId]);
  const resolvedProductId = firstDefined(raw, [(entry) => entry?.iProductId]);
  const resolvedBackgroundId = firstDefined(raw, [(entry) => entry?.iBackgroundId]);
  const resolvedGenerationConfig = firstDefined(raw, [
    (entry) => entry?.sGenerationConfig,
    (entry) => entry?.generationConfig,
    (entry) => entry?.aGenerationConfig,
    (entry) => entry?.image_data,
  ]);

  const payload = {
    sStatus: "queued",
  };

  if (resolvedModeId !== undefined && resolvedModeId !== null && resolvedModeId !== "") {
    payload.iModeId = resolvedModeId;
  }
  if (resolvedGenderId !== undefined && resolvedGenderId !== null && resolvedGenderId !== "") {
    payload.iGenderId = resolvedGenderId;
  }
  if (resolvedModelId !== undefined && resolvedModelId !== null && resolvedModelId !== "") {
    payload.iModelId = resolvedModelId;
  }
  if (resolvedProductId !== undefined && resolvedProductId !== null && resolvedProductId !== "") {
    payload.iProductId = resolvedProductId;
  }
  if (resolvedBackgroundId !== undefined && resolvedBackgroundId !== null && resolvedBackgroundId !== "") {
    payload.iBackgroundId = resolvedBackgroundId;
  }
  if (resolvedGenerationConfig !== undefined && resolvedGenerationConfig !== null && resolvedGenerationConfig !== "") {
    payload.sGenerationConfig = resolvedGenerationConfig;
  }

  return payload;
};

export default function useQueue() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);

  // Search & Filter UI States
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  // Queue Data States
  const [queueItems, setQueueItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(10);
  const [queueTotal, setQueueTotal] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasLoadedQueueItems, setHasLoadedQueueItems] = useState(false);
  const [apiStats, setApiStats] = useState({ total: 0, processing: 0, queued: 0, completed: 0, failed: 0 });
  const [apiFilters, setApiFilters] = useState([]);

  // Zoom Modal State
  const [zoomModal, setZoomModal] = useState({
    front: "",
    back: "",
    showFlip: false,
    isBackground: false,
    isMobileOpen: false,
  });

  // Regeneration state
  const [regeneratingQueue, setRegeneratingQueue] = useState({});
  const generationTriggerRef = useRef(false);
  const processedQueueIdsRef = useRef(new Set());

  // Header Title
  useEffect(() => {
    dispatch(saveHeaderTitle("Queue"));
  }, [dispatch]);

  // Search Debounce
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  // Reset page when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, activeFilter]);

  // Zoom Modal Controls
  const isMobileViewport = () =>
    typeof window !== "undefined" && window.innerWidth < 768;

  const openZoomModal = (front, back = "", showFlip = false, isBackground = false) => {
    setZoomModal({
      front,
      back,
      showFlip,
      isBackground,
      isMobileOpen: isMobileViewport(),
    });
  };

  const closeZoomModal = () => {
    setZoomModal({
      front: "",
      back: "",
      showFlip: false,
      isBackground: false,
      isMobileOpen: false,
    });
  };

  // Filter Change Handler
  const handleFilterChange = (filterObj) => {
    let nextStatus = "all";
    const rawStatus =
      filterObj?.status ??
      filterObj?.Status ??
      filterObj?.sStatus ??
      filterObj?.categories;

    if (rawStatus) {
      if (Array.isArray(rawStatus)) {
        const clean = rawStatus.filter((s) => String(s).toLowerCase() !== "all");
        if (clean.length === 0) {
          nextStatus = "all";
        } else {
          nextStatus = clean.join(",");
        }
      } else if (typeof rawStatus === "string") {
        nextStatus = rawStatus.toLowerCase() === "all" ? "all" : rawStatus;
      }
    }

    if (nextStatus !== activeFilter) {
      setActiveFilter(nextStatus);
    }
  };

  const fetchQueueItems = useCallback(
    async ({ silent = false } = {}) => {
      if (!token) {
        setQueueItems([]);
        setHasLoadedQueueItems(true);
        setApiStats({ total: 0, processing: 0, queued: 0, completed: 0, failed: 0 });
        setApiFilters([]);
        dispatch(resetQueueStats());
        return [];
      }

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const response = await apiHandler({
          url: buildQueueListUrl({ page, limit: pageSize, search: debouncedSearchTerm, status: activeFilter }),
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const items = extractQueueItems(response);
        const normalizedItems = items.map(normalizeQueueStateItem);
        const paginationMeta = extractQueuePaginationMeta(response, pageSize);

        const payload = response?.data?.data ?? response?.data ?? {};
        const stats = payload?.stats;
        if (stats) {
          setApiStats({
            total: Number(stats.total) || 0,
            processing: Number(stats.processing) || 0,
            queued: Number(stats.queued) || 0,
            completed: Number(stats.completed) || 0,
            failed: Number(stats.failed) || 0,
          });
        } else {
          setApiStats(null);
        }

        const returnedFilters = payload?.filters || response?.data?.filters || null;
        if (Array.isArray(returnedFilters)) {
          setApiFilters(returnedFilters);
        }

        setQueueItems(normalizedItems);
        setQueueTotal(paginationMeta.total);
        setApiTotalPages(paginationMeta.totalPages);
        setHasLoadedQueueItems(true);
        setError("");
        return normalizedItems;
      } catch (fetchError) {
        console.error("Failed to fetch queue items:", fetchError);
        setError("Unable to load queue items.");
        return [];
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [token, dispatch, page, pageSize, debouncedSearchTerm, activeFilter]
  );

  const getGenerationStatus = useCallback(
    async (queueId) => {
      if (!queueId) return null;

      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}/${queueId}${endpoint.QUEUE_TASKS_GENERATION_STATUS}`,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        const payload = response?.data?.data ?? response?.data ?? {};
        const status = asString(
          firstDefined(payload, [
            (entry) => entry?.sStatus,
            (entry) => entry?.status,
            (entry) => entry?.generation_status,
          ]),
          ""
        ).toLowerCase();

        return { payload, status };
      } catch (error) {
        console.error("Failed to fetch generation status:", error);
        return null;
      }
    },
    [token]
  );

  const startGeneration = useCallback(
    async (queueItem) => {
      if (!queueItem?.id) return;

      processedQueueIdsRef.current.add(queueItem.id);

      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}/${queueItem.id}${endpoint.QUEUE_TASKS_GENERATE}`,
          method: "POST",
          authToken: { token },
        });

        if (response?.status >= 200 && response?.status < 300) {
          dispatch(moveQueueStatus({ from: "queued", to: "processing" }));
        }
      } catch (generationError) {
        console.error("Queue generation trigger failed:", generationError);
        processedQueueIdsRef.current.delete(queueItem.id);
      } finally {
        generationTriggerRef.current = false;
        await fetchQueueItems({ silent: true });
      }
    },
    [token, dispatch, fetchQueueItems]
  );

  const regenerateQueueTask = useCallback(
    async (queueTaskId) => {
      const sourceItem = queueItems.find((item) => String(getQueueItemId(item)) === String(queueTaskId));

      if (!sourceItem) {
        throw new Error("Queue item not found.");
      }

      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}`,
        method: "POST",
        data: buildQueuePayloadFromItem(sourceItem),
        authToken: { token },
      });

      if (response?.status >= 200 && response?.status < 300) {
        const queuePayload = response?.data?.data ?? response?.data ?? {};
        const queueId =
          queuePayload?.iQueueId ??
          queuePayload?.id ??
          queuePayload?.iQueueTaskId ??
          queuePayload?.queueTaskId;
        const queueStatus = String(queuePayload?.status ?? queuePayload?.sStatus ?? "").toLowerCase();

        if (queueId && queueStatus === "queued") {
          try {
            await apiHandler({
              url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}/${queueId}${endpoint.QUEUE_TASKS_GENERATE}`,
              method: "POST",
              authToken: { token },
            });
            dispatch(moveQueueStatus({ from: "queued", to: "processing" }));
          } catch (generationError) {
            console.error("Failed to trigger regeneration generation:", generationError);
          }
        }

        await fetchQueueItems({ silent: true });
        triggerGenerationCountRefresh();
      }
    },
    [token, queueItems, dispatch, fetchQueueItems]
  );

  const handleRegenerate = async (queueTaskId, previousGeneratedImage = "") => {
    if (!queueTaskId) return;

    setRegeneratingQueue((prev) => ({ ...prev, [queueTaskId]: previousGeneratedImage }));

    try {
      await regenerateQueueTask(queueTaskId);
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    } catch (err) {
      console.error("Failed to regenerate/retry task:", err);
      setRegeneratingQueue((prev) => {
        const copy = { ...prev };
        delete copy[queueTaskId];
        return copy;
      });
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    }
  };

  // Regeneration state listener
  useEffect(() => {
    const ids = Object.keys(regeneratingQueue || {});
    if (!ids.length) return;

    const terminalStatuses = new Set(["completed", "failed", "error"]);
    const updatedMap = { ...regeneratingQueue };
    let changed = false;

    ids.forEach((id) => {
      const refreshedItem = queueItems.find((item) => String(item.id) === String(id));
      if (!refreshedItem) return;

      const refreshedImage = refreshedItem?.result?.generatedImage || refreshedItem?.result?.image || "";
      const prevImage = regeneratingQueue[id] || "";

      if (terminalStatuses.has(String(refreshedItem.status || "").toLowerCase())) {
        delete updatedMap[id];
        changed = true;
        return;
      }

      if (refreshedItem.status !== "processing" && refreshedImage && refreshedImage !== prevImage) {
        delete updatedMap[id];
        changed = true;
      }
    });

    if (changed) {
      setRegeneratingQueue(updatedMap);
    }
  }, [queueItems, regeneratingQueue]);

  // Initial fetch when token or page/filter changes
  useEffect(() => {
    setHasLoadedQueueItems(false);
    fetchQueueItems();
  }, [fetchQueueItems]);

  // Trigger generation for queued items
  useEffect(() => {
    if (generationTriggerRef.current) return;
    if (!Array.isArray(queueItems) || queueItems.length === 0) return;

    const nextQueueItem = queueItems.find(
      (item) => item?.status === "queued" && item?.id && !processedQueueIdsRef.current.has(item.id)
    );

    if (!nextQueueItem?.id) return;

    generationTriggerRef.current = true;
    startGeneration(nextQueueItem);
  }, [queueItems, startGeneration]);

  // Poll for generation status updates
  useEffect(() => {
    if (!Array.isArray(queueItems) || queueItems.length === 0) return undefined;

    const processingItem = queueItems.find(
      (item) => item?.status === "processing" && item?.id
    );

    if (!processingItem?.id) return undefined;

    let isStale = false;
    const pollInterval = setInterval(async () => {
      if (isStale) return;

      try {
        const statusResult = await getGenerationStatus(processingItem.id);
        const currentStatus = statusResult?.status || "";

        if (["completed", "failed", "error"].includes(currentStatus)) {
          isStale = true;
          setQueueItems((prevItems) =>
            prevItems.map((item) =>
              String(getQueueItemId(item)) === String(processingItem.id)
                ? normalizeQueueStateItem(
                    mergeQueueTerminalStatus(item, processingItem.id, statusResult)
                  )
                : item
            )
          );
          await fetchQueueItems({ silent: true });
          triggerGenerationCountRefresh();
        }
      } catch (pollError) {
        console.error("Failed to poll generation status:", pollError);
      }
    }, 2500);

    return () => {
      isStale = true;
      clearInterval(pollInterval);
    };
  }, [queueItems, getGenerationStatus, fetchQueueItems]);

  // Normalize items for display
  const normalizedItems = useMemo(() => queueItems.map((item) => normalizeQueueItem(item)), [queueItems]);

  // Active vs History grouping
  const visibleItems = normalizedItems;
  const activeItems = useMemo(
    () => visibleItems.filter((item) => ACTIVE_STATUSES.has(String(item.status || "").toLowerCase())),
    [visibleItems]
  );
  const historyItems = useMemo(
    () => visibleItems.filter((item) => !ACTIVE_STATUSES.has(String(item.status || "").toLowerCase())),
    [visibleItems]
  );

  // Stats across all items
  const queueStats = useMemo(() => {
    if (apiStats) {
      return apiStats;
    }
    const total = queueTotal;
    const processing = normalizedItems.filter((item) => item.status === "processing").length;
    const queued = normalizedItems.filter((item) => item.status === "queued").length;
    const completed = normalizedItems.filter((item) => item.status === "completed").length;
    const failed = normalizedItems.filter((item) => item.status === "failed").length;

    return { total, processing, queued, completed, failed };
  }, [normalizedItems, queueTotal, apiStats]);

  useEffect(() => {
    if (!hasLoadedQueueItems) return;
    dispatch(saveQueueStats(queueStats));
  }, [dispatch, hasLoadedQueueItems, queueStats]);

  const totalPages = apiTotalPages;

  // Clamp page when backend total changes
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const setPageSize = useCallback((nextPageSize) => {
    setPageSizeState(nextPageSize);
    setPage(1);
  }, []);

  const filterCount = (key) => (key === "all" ? queueStats.total : queueStats[key] || 0);
  const hasVisibleItems = visibleItems.length > 0;

  const queueFilters = Array.isArray(apiFilters) && apiFilters.length > 0
    ? apiFilters
    : [
        {
          label: "Status",
          key: "status",
          isMultiple: true,
          options: STATUS_FILTERS.map((f) => ({ value: f.key, label: f.label })),
        },
      ];

  const getQueueItemProps = (item, index) => {
    const isRegenerating = Object.prototype.hasOwnProperty.call(regeneratingQueue, item.id);
    const prevImage = regeneratingQueue[item.id] || "";
    const hasNewGeneratedImage = isRegenerating
      ? Boolean((item.result?.generatedImage || item.result?.image) && (item.result?.generatedImage || item.result?.image) !== prevImage)
      : false;
    const forceLoader = item.showLoader || (isRegenerating && !hasNewGeneratedImage);

    return {
      key: item.id || index,
      index,
      id: item.id,
      product: item.product,
      model: item.model,
      background: item.background,
      generationParameters: item.generationParameters,
      result: isRegenerating && !hasNewGeneratedImage ? { image: "", generatedImage: "" } : item.result,
      date: item.date,
      startTime: item.startTime,
      executionTime: item.executionTime,
      status: forceLoader ? "processing" : item.status,
      generation_status: item.generation_status,
      generationStatus: item.generation_status,
      errorMessage: item.displayErrorMessage || item.errorMessage,
      displayErrorMessage: item.displayErrorMessage || item.errorMessage,
      error_message: item.error_message,
      progress: item.progress,
      showLoader: forceLoader,
      showProgress: item.showProgress,
      buttons: item.buttons,
      onZoom: openZoomModal,
      onRegenerate: handleRegenerate,
    };
  };

  return {
    searchTerm,
    setSearchTerm,
    activeFilter,
    handleFilterChange,
    queueFilters,
    queueStats,
    filterCount,
    isLoading,
    error,
    hasVisibleItems,
    visibleItems,
    activeItems,
    historyItems,
    pagination: {
      page,
      pageSize,
      totalPages,
      total: queueStats.total,
      setPage,
      setPageSize,
    },
    zoomModal,
    openZoomModal,
    closeZoomModal,
    handleRegenerate,
    getQueueItemProps,
    refreshQueue: fetchQueueItems,
  };
}
